<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrIcon from "~/components/global/WrIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface LevelUpCard {
  svgIcon?: string;
  icon?: string;
  title: string;
  description: string;
}

const props = withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    cards: LevelUpCard[];
    ctaText?: string;
    ctaLink?: string | null;
    iconSize?: string;
    spriteIcon?: boolean;
  }>(),
  {
    ctaText: "Explore More",
    ctaLink: null,
    iconSize: "50px",
    spriteIcon: false,
  },
);

const emit = defineEmits<{
  "cta-clicked": [];
}>();
</script>

<template>
  <section
    class="mt-24 lg:mt-[150px] mb-16 lg:mb-[100px]"
    :aria-label="heading"
  >
    <!-- Section heading -->
    <div class="text-center mb-10">
      <h2
        class="text-[28px] lg:text-[48px] font-semibold leading-tight font-avant-demi bg-gradient-to-r from-[#12DBFF] to-[#BA55FE] bg-clip-text text-transparent"
        v-html="heading"
      ></h2>
      <p
        class="mt-3 max-w-[500px] mx-auto text-[16px] lg:text-[18px] font-normal tracking-[0.36px] font-avant"
        :class="isLight ? 'text-black opacity-70' : 'text-white opacity-70'"
      >
        {{ subcopy }}
      </p>
    </div>

    <!-- Cards Grid -->
    <div
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-10 px-4 max-w-[1200px] mx-auto"
    >
      <article
        v-for="card in cards"
        :key="card.title"
        class="level-up-card flex flex-col p-4 gap-6 max-sm:gap-4"
        :style="
          isLight
            ? {
                border: '1px solid rgba(208, 208, 208, 0.5)',
                background: `url('/images/Why_Clients_Love_Working_With_Us_bg_Lighttheme.svg') no-repeat center / cover, linear-gradient(32deg, #FFF -1.26%, #FFF 100.92%)`,
                boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.04)',
              }
            : {
                border: '1px solid #3D1B60',
                background: `url('/images/Why_Clients_Love_Working_With_Us_bg.svg') no-repeat center / cover, linear-gradient(32deg, #010314 -1.26%, #2B0152 100.92%)`,
              }
        "
      >
        <!-- Card Icon -->
        <div class="flex-shrink-0">
          <WrIcon
            v-if="card.svgIcon && card.svgIcon.startsWith('streamline:')"
            :name="card?.svgIcon"
            class="size-6 sm:size-8 text-[#501D71] dark:text-white"
          />
          <WrSVGIcon
            v-else-if="spriteIcon && card.svgIcon"
            :name="card.svgIcon"
            class="size-11 dark:text-white text-[#501D71]"
          />
          <WrSpriteIcon
            v-else-if="card.icon"
            :light="`/static/icons/Design-Pages-icons-sprite.svg#icon-levelup-${card.icon}-light`"
            :dark="`/static/icons/Design-Pages-icons-sprite.svg#icon-levelup-${card.icon}`"
            :style="{ width: props.iconSize, height: props.iconSize }"
          />
        </div>
        <h3
          class="self-stretch text-left font-avant text-[20px] font-normal min-h-14 max-sm:min-h-6"
          :class="isLight ? 'text-[#613FAA]' : 'text-white'"
        >
          {{ card.title }}
        </h3>
        <p
          class="self-stretch text-left font-avant text-[15px] font-normal tracking-[0.3px]"
          :class="isLight ? 'text-black' : 'text-white'"
        >
          {{ card.description }}
        </p>
      </article>
    </div>

    <!-- CTA Button -->
    <div class="flex justify-center mt-8">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-[48px]"
        append-class="text-base font-normal px-6"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>

<style scoped>
.level-up-card {
  border-radius: 8px;
  position: relative;
  height: auto;
}

@media (min-width: 1024px) {
  .level-up-card {
    min-height: 263px;
  }
}
</style>
